import React, { useState } from "react";
import { ChevronDown, ChevronRight, WorkflowIcon, Trash2Icon, CheckCircle } from "lucide-react";

const ChainAccordion = ({ item, onDelete, hideDelete = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { chainDetails } = item;

  const steps = chainDetails.steps ? JSON.parse(chainDetails.steps) : [];

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":
        return "text-green-600 bg-green-50 border-green-200";
      case "POST":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "PUT":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "DELETE":
        return "text-red-600 bg-red-50 border-red-200";
      case "PATCH":
        return "text-purple-600 bg-purple-50 border-purple-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const formatInputParameters = (params) => {
    if (!params || typeof params !== "object") return "";
    return Object.entries(params)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(", ");
  };

  return (
    <div
      className="border cursor-pointer border-gray-200 rounded-lg overflow-hidden "
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Chain Header */}
      <div className="p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            {isExpanded ? (
              <ChevronDown size={16} className="text-gray-500" />
            ) : (
              <ChevronRight size={16} className="text-gray-500" />
            )}
            <WorkflowIcon size={16} className="text-purple-500" />

            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Chain: {chainDetails.name}</h4>
              {chainDetails.description && (
                <p className="text-xs text-gray-500 mt-1">{chainDetails.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2">
                <span className="text-xs text-gray-400">{steps.length} steps</span>
                <span className="text-xs text-gray-400">
                  Type: {chainDetails.test_type || "integration"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {!hideDelete && (
              <button
                onClick={() => onDelete?.(item.id)}
                className="text-red-500 hover:text-red-700 cursor-pointer"
              >
                <Trash2Icon size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Chain Steps Flow */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={16} className="text-green-500" />
                <span className="text-sm font-medium text-gray-700">
                  Validation: {chainDetails.validation_criteria}
                </span>
              </div>

              {steps.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No steps defined for this chain
                </div>
              ) : (
                <div className="space-y-6">
                  {steps.map((step, index) => (
                    <div key={step.step_number || index} className="relative">
                      {/* Step Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start gap-4">
                          {/* Step Number */}
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-semibold">
                              {step.step_number}
                            </div>
                          </div>

                          {/* Step Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded border ${getMethodColor(step.http_method)}`}
                              >
                                {step.http_method}
                              </span>
                              <code className="text-sm text-gray-800 bg-gray-100 px-2 py-1 rounded">
                                {step.endpoint}
                              </code>
                            </div>

                            <h5 className="text-sm font-medium text-gray-900 mb-2">
                              {step.test_case}
                            </h5>

                            {/* Parameters */}
                            {step.input_parameters &&
                              Object.keys(step.input_parameters).length > 0 && (
                                <div className="mb-2">
                                  <span className="text-xs text-gray-600">Parameters: </span>
                                  <span className="text-xs text-gray-500 font-mono">
                                    {formatInputParameters(step.input_parameters)}
                                  </span>
                                </div>
                              )}

                            {/* Expected Result */}
                            <div className="grid grid-cols-2 gap-4 text-xs">
                              <div>
                                <span className="text-gray-600">Expected Status: </span>
                                <span className="font-medium text-gray-800">
                                  {step.expected_status_code}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Expected Result: </span>
                                <span className="font-medium text-gray-800">
                                  {step.expected_result}
                                </span>
                              </div>
                            </div>

                            {/* Output Mapping */}
                            {step.output_mapping && Object.keys(step.output_mapping).length > 0 && (
                              <div className="mt-2 text-xs">
                                <span className="text-gray-600">Output Mapping: </span>
                                <span className="font-mono text-gray-500">
                                  {step.output_mapping.source_field} →{" "}
                                  {step.output_mapping.destination}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChainAccordion;
